import { BitkitControlButton, BitkitSpinner, IconChevronDown, IconChevronUp } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Group } from '@chakra-ui/react/group';
import { Text } from '@chakra-ui/react/text';
import { memo, type RefObject, useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { getErrorMarkerCount, jumpToError } from '@/components/LogViewer/LogErrorMarker';
import { INITIAL_STATE, logViewerReducer } from '@/components/LogViewer/LogViewerReducer';

import LogChunk from './LogChunk';
import { type ColorizeChunk, processLog, ProgressResult } from './LogProcessor';
import LogViewerToolbar from './LogViewerToolbar';

const ProgressStatus = ({ progress }: { progress?: ProgressResult }) => {
  if (!progress || progress.value === 100) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap="8" color="text/body">
      <BitkitSpinner width="16" height="16" aria-hidden />
      <Text>
        Rendering logs: {progress.value}% [{progress.chunkIndex}/{progress.chunkCount}]
      </Text>
    </Box>
  );
};

const ErrorActions = ({
  errorCount,
  onJumpToPrevious,
  onJumpToNext,
}: {
  errorCount: number;
  onJumpToPrevious: VoidFunction;
  onJumpToNext: VoidFunction;
}) => {
  if (errorCount <= 0) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap="4">
      <Text textStyle="comp/badge/sm" color="status/critical/text">
        {errorCount} {errorCount > 1 ? 'errors' : 'error'}
      </Text>
      <Group gap="0">
        <BitkitControlButton size="xs" icon={IconChevronUp} label="Previous error" onClick={onJumpToPrevious} />
        <BitkitControlButton size="xs" icon={IconChevronDown} label="Next error" onClick={onJumpToNext} />
      </Group>
    </Box>
  );
};

type LogViewerProps = BoxProps & {
  content: string;
  stickyToolbarOffset?: 'platform-header' | 'none';
  scrollTargetRef?: RefObject<HTMLElement | null>;
  downloadUrl?: string;
  onScrollNavigate?: (direction: 'up' | 'down') => void;
};

const LogViewer = ({
  content,
  stickyToolbarOffset = 'platform-header',
  scrollTargetRef,
  downloadUrl,
  onScrollNavigate,
  ...rest
}: LogViewerProps) => {
  const logViewerRef = useRef<HTMLDivElement>(null);
  const colorizeChunkRef = useRef<ColorizeChunk | null>(null);
  const [errorCount, setErrorCount] = useState(0);
  const [{ progress, rawChunks, lineWrapping, toolbarDisabled, errorMarkerIndex }, dispatch] = useReducer(
    logViewerReducer,
    INITIAL_STATE,
  );

  const handleWrapChange = useCallback((wrap: boolean) => {
    dispatch({ type: 'CHANGE_LINE_WRAPPING', payload: wrap });
  }, []);

  // Each chunk pulls its own colorized HTML on demand once it scrolls into view.
  const colorize = useCallback((index: number) => colorizeChunkRef.current?.(index)?.content, []);

  const handleJumpToPreviousError = useCallback(() => {
    const count = getErrorMarkerCount(logViewerRef.current);
    if (count) {
      const jumpIndex = errorMarkerIndex - 1 < 0 ? count - 1 : errorMarkerIndex - 1;
      jumpToError(logViewerRef.current, jumpIndex);
      dispatch({ type: 'SET_CURRENT_ERROR_MARKER', payload: jumpIndex });
    }
  }, [errorMarkerIndex]);

  useEffect(() => {
    setErrorCount(toolbarDisabled ? 0 : getErrorMarkerCount(logViewerRef.current));
  }, [toolbarDisabled]);

  const handleJumpToNextError = useCallback(() => {
    const count = getErrorMarkerCount(logViewerRef.current);
    if (count) {
      const jumpIndex = errorMarkerIndex + 1 >= count ? 0 : errorMarkerIndex + 1;
      jumpToError(logViewerRef.current, jumpIndex);
      dispatch({ type: 'SET_CURRENT_ERROR_MARKER', payload: jumpIndex });
    }
  }, [errorMarkerIndex]);

  useEffect(() => {
    const abortController = new AbortController();
    dispatch({ type: 'INITIALIZE_PROCESSING' });

    colorizeChunkRef.current = processLog(content, {
      signal: abortController.signal,
      onProgress: (p) => dispatch({ type: 'UPDATE_PROGRESS', payload: p }),
      onRawChunk: (chunk) => dispatch({ type: 'PROCESS_RAW_CHUNK', payload: chunk }),
      onSettled: (completed) => {
        if (completed) {
          dispatch({ type: 'COMPLETE_PROCESSING' });
        }
      },
    });

    return () => {
      abortController.abort();
      colorizeChunkRef.current = null;
    };
  }, [content]);

  return (
    <Box
      ref={logViewerRef}
      color="text/on-color"
      className="log-viewer"
      background="background/fixed-contrast"
      borderBottomRadius="4"
      {...rest}
    >
      <LogViewerToolbar
        offset={stickyToolbarOffset}
        isDisabled={toolbarDisabled}
        scrollTargetRef={scrollTargetRef}
        downloadUrl={downloadUrl}
        wrap={lineWrapping}
        onWrapChanged={handleWrapChange}
        onScrollNavigate={onScrollNavigate}
      >
        <ProgressStatus progress={progress} />
        <ErrorActions
          errorCount={errorCount}
          onJumpToNext={handleJumpToNextError}
          onJumpToPrevious={handleJumpToPreviousError}
        />
      </LogViewerToolbar>
      <Box px="16" py="8" overflow="auto">
        {rawChunks.map(({ index, range, content: chunkContent }) => (
          <LogChunk
            key={`${index}-${range[0]}-${range[1]}`}
            wrap={lineWrapping}
            index={index}
            range={range}
            content={chunkContent}
            colorize={colorize}
          />
        ))}
      </Box>
    </Box>
  );
};

const MemoizedLogViewer = memo(LogViewer);

export default MemoizedLogViewer;
